import "./ClubPlanCustomersPage.scss";

import { Badge, Col, Row } from "react-bootstrap";
import React, { useEffect, useMemo, useState } from "react";
import {
  faBan,
  faCheckCircle,
  faClock,
  faCopy,
  faHourglassHalf,
  faLink,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";

import AppLoading from "../../../../../commons/AppLoading";
import ClubSubscriptionAPI from "../../api/ClubSubscriptionAPI";
import ConfirmModal from "../../../../../commons/modal/ConfirmModal";
import CustomCard from "../../../../../commons/cards/Card";
import EmptyState from "../../../../../commons/EmptyState";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import HallAppToast from "../../../../../commons/HallAppToast";
import MenuTitle from "../../../../../commons/menu/MenuTitle";
import ReportHeader from "../../../../report-full/components/ReportHeader";
import { useParams } from "react-router-dom";

const api = new ClubSubscriptionAPI();

function formatDateTime(v) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("pt-BR");
  } catch {
    return "—";
  }
}

function statusLabel(v) {
  switch (v) {
    case "PENDING":
      return "Pendente";
    case "SENT":
      return "Enviado";
    case "ACTIVE":
      return "Ativo";
    case "PAUSED":
      return "Pausado";
    case "CANCELED":
      return "Cancelado";
    case "EXPIRED":
      return "Expirado";
    case "FAILED":
      return "Falhou";
    default:
      return v || "—";
  }
}

function statusVariant(v) {
  switch (v) {
    case "ACTIVE":
      return "success";
    case "PENDING":
      return "warning";
    case "SENT":
      return "info";
    case "PAUSED":
      return "secondary";
    case "CANCELED":
      return "danger";
    case "EXPIRED":
    case "FAILED":
      return "dark";
    default:
      return "light";
  }
}

function ActionPillButton({
  color,
  borderColor,
  icon,
  label,
  onClick,
  title,
  disabled,
  loading,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled || loading}
      className="club-pill-btn"
      style={{
        color: color || "#111827",
        borderColor: borderColor || "#e8e8f0",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {loading ? (
        <span className="club-pill-spinner" aria-hidden="true" />
      ) : (
        <FontAwesomeIcon icon={icon} />
      )}
      {label}
    </button>
  );
}

function CustomerSubscriptionCard({
  it,
  isGenerating,
  isCanceling,
  onGenerateLink,
  onCopyLink,
  onAskCancel,
}) {
  const hasLink = !!it.checkoutUrl;
  const canGenerate = it.status === "PENDING" && !hasLink;
  const canCancel = ["ACTIVE", "SENT", "PAUSED"].includes(it.status);

  const sub = useMemo(() => it.customerWhatsapp || "Sem WhatsApp", [it.customerWhatsapp]);

  const invited = it.invitedDt || it.linkedAt;
  const subscribed = it.subscribedDt || it.acceptedAt;
  const created = it.createdDt || it.createdAt;

  return (
    <CustomCard>
      <div className="club-customer-card2">
        <div className="club-customer-card2__header">
          <div className="club-customer-card2__title">
            <div className="club-customer-card2__name">
              {it.customerName || `Customer #${it.customerId}`}
            </div>
            <div className="club-customer-card2__sub">{sub}</div>
          </div>

          <div className="club-customer-card2__badges">
            <Badge bg={statusVariant(it.status)} pill className="club-badge">
              {it.status === "ACTIVE" ? (
                <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
              ) : (
                <FontAwesomeIcon icon={faHourglassHalf} className="me-1" />
              )}
              {statusLabel(it.status)}
            </Badge>

            {hasLink && (
              <Badge bg="light" text="dark" pill className="club-badge">
                <FontAwesomeIcon icon={faLink} className="me-1" />
                Link gerado
              </Badge>
            )}
          </div>
        </div>

        <hr className="club-hr" />

        <div className="club-customer-card2__info">
          <div className="club-info-row">
            <div className="club-info-left">
              <FontAwesomeIcon icon={faClock} />
              <span>Vinculado em</span>
            </div>
            <div className="club-info-right">{formatDateTime(invited)}</div>
          </div>

          <div className="club-info-row">
            <div className="club-info-left">
              <FontAwesomeIcon icon={faCheckCircle} />
              <span>Aceito em</span>
            </div>
            <div className="club-info-right">{formatDateTime(subscribed)}</div>
          </div>

          <div className="club-info-row">
            <div className="club-info-left">
              <span style={{ fontWeight: 900 }}>#</span>
              <span>Criado em</span>
            </div>
            <div className="club-info-right">{formatDateTime(created)}</div>
          </div>
        </div>

        <div className="club-customer-card2__actions">
          {canGenerate && (
            <ActionPillButton
              icon={faLink}
              label="Gerar link"
              onClick={() => onGenerateLink(it.subscriptionId || it.id)}
              loading={isGenerating}
              color="#059669"
              borderColor="#d1fae5"
              title="Gerar link de assinatura"
            />
          )}

          {hasLink && (
            <ActionPillButton
              icon={faCopy}
              label="Copiar link"
              onClick={() => onCopyLink(it.checkoutUrl)}
              color="#2f2a7a"
              borderColor="#dfe3ff"
              title="Copiar link"
            />
          )}

          {canCancel && (
            <ActionPillButton
              icon={faBan}
              label="Cancelar"
              onClick={() => onAskCancel(it.subscriptionId || it.id)}
              loading={isCanceling}
              color="#b42318"
              borderColor="#ffd6d6"
              title="Cancelar assinatura"
            />
          )}
        </div>

        {it.status === "PENDING" && !hasLink && (
          <div className="club-customer-card2__hint">
            Dica: gere o link e envie para o cliente finalizar a assinatura.
          </div>
        )}
      </div>
    </CustomCard>
  );
}

function ClubPlanCustomersPage() {
  const { clubPlanId } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [items, setItems] = useState([]);

  const [isGeneratingId, setIsGeneratingId] = useState(null);

  // Cancel via ConfirmModal
  const [showModalCancel, setShowModalCancel] = useState(false);
  const [isLoadingModalCancel, setIsLoadingModalCancel] = useState(false);
  const [pendingCancelId, setPendingCancelId] = useState(null);

  const pendingCancelItem = useMemo(() => {
    if (!pendingCancelId) return null;
    return items.find((x) => (x.subscriptionId || x.id) === pendingCancelId) || null;
  }, [pendingCancelId, items]);

  async function load() {
    try {
      setIsLoading(true);
      const res = await api.getCustomersByClub(clubPlanId);
      const data = res?.data || res;
      const list = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
      setItems(list);
    } catch (e) {
      setItems([]);
      HallAppToast.genericError();
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubPlanId]);

  async function handleGenerateLink(subscriptionId) {
    try {
      setIsGeneratingId(subscriptionId);

      const res = await api.generateLink(subscriptionId);
      const data = res?.data || res;

      HallAppToast.success("Link gerado com sucesso.");

      setItems((prev) =>
        prev.map((it) => {
          const id = it.subscriptionId || it.id;
          return id === subscriptionId
            ? { ...it, checkoutUrl: data.checkoutUrl ?? it.checkoutUrl, status: data.status ?? it.status }
            : it;
        })
      );
    } catch (e) {
      HallAppToast.error("Verifique se o cliente possui e-mail cadastrado");
    } finally {
      setIsGeneratingId(null);
    }
  }

  async function handleCopyLink(url) {
    try {
      await navigator.clipboard.writeText(url);
      HallAppToast.success("Link copiado.");
    } catch {
      HallAppToast.warning("Não foi possível copiar automaticamente. Copie manualmente.");
    }
  }

  function askCancel(subscriptionId) {
    setPendingCancelId(subscriptionId);
    setShowModalCancel(true);
  }

  async function executeCancel() {
    if (!pendingCancelId) return;

    try {
      setIsLoadingModalCancel(true);

      await api.cancel(pendingCancelId);

      HallAppToast.success("Assinatura cancelada com sucesso.");

      // Atualiza UI
      setItems((prev) =>
        prev.map((it) => {
          const id = it.subscriptionId || it.id;
          return id === pendingCancelId
            ? { ...it, status: "CANCELED" }
            : it;
        })
      );

      setShowModalCancel(false);
      setPendingCancelId(null);
    } catch (e) {
      HallAppToast.genericError();
    } finally {
      setIsLoadingModalCancel(false);
    }
  }

  return (
    <div className="container club-plan-customers-page">
      <ReportHeader name="Clientes do Clube" />
      <MenuTitle
        title="Clientes do Clube"
        subtitle="Gerencie assinaturas e gere links de pagamento."
        showArrowInfo={false}
      />

      {isLoading ? (
        <AppLoading />
      ) : items.length === 0 ? (
        <EmptyState
          title="Nenhum cliente vinculado"
          description="Vincule clientes ao plano para gerar links de assinatura."
          icon={<FontAwesomeIcon icon={faUsers} />}
        />
      ) : (
        <Row className="g-3" style={{ margin: 0, width: "100%" }}>
          {items.map((it) => {
            const id = it.subscriptionId || it.id;
            return (
              <Col xs={12} md={6} lg={4} key={id}>
                <CustomerSubscriptionCard
                  it={it}
                  isGenerating={isGeneratingId === id}
                  isCanceling={false} // loading do cancel fica no modal (não no card)
                  onGenerateLink={handleGenerateLink}
                  onCopyLink={handleCopyLink}
                  onAskCancel={askCancel}
                />
              </Col>
            );
          })}
        </Row>
      )}

      {showModalCancel && (
        <ConfirmModal
          show={showModalCancel}
          title="Cancelar assinatura"
          isLoading={isLoadingModalCancel}
          confirm={executeCancel}
          closeModal={() => {
            setShowModalCancel(false);
            setPendingCancelId(null);
          }}
        >
          Deseja cancelar a assinatura de{" "}
          <strong>{pendingCancelItem?.customerName || "este cliente"}</strong>?
          <br />
          <span className="text-muted" style={{ fontSize: 12 }}>
            Isso interrompe cobranças futuras no Mercado Pago.
          </span>
        </ConfirmModal>
      )}
    </div>
  );
}

export default ClubPlanCustomersPage;
